import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Menu, MenuItem } from '@mui/material';
import { Link, useHistory } from 'react-router-dom';

import Text from '@components/v2/ui-elements/Text';
import { BreadcrumbData, buildBreadcrumbUrl, useBreadcrumbs } from '@root/hooks/breadcrumbs/useBreadcrumbs';
import { ReactComponent as ChevronIcon } from '@assets/svg/breadcrumb/chevron-right.svg';
// import insights_selected from '@root/assets/svg/icons/new/selected/Insights.svg';
// import scan_selected from '@root/assets/svg/icons/new/selected/Scan.svg';

import './style.scss';
import { BreadcrumbIconMapping } from '@root/assets/img/breadcrumb-icons/BreadcrumIconMappings';
import { useTheme } from '@root/context/ThemeContext';

declare const window: any;

// `pageTitle` pins the <h1> to the page's own name. Without it the heading is the
// first crumb, which is the page the user came from once a trail is carried in the
// URL — right for a section that is part of that parent, wrong for a page that
// stands on its own and only wants the trail to say where the visit started.
function Breadcrumbs({ breadcrumbs, pageTitle }: { breadcrumbs?: BreadcrumbData[]; pageTitle?: string }) {
  const history = useHistory();
  const { breadcrumbData } = useBreadcrumbs();
  const anchorElRef = useRef();
  const [showHiddenItems, setShowHiddenItems] = useState(false);
  const { isDarkMode } = useTheme();

  useEffect(() => {
    if (breadcrumbs) {
      window.BREADCRUMB_PROPS = { breadcrumbs };
    }

    return () => {
      window.BREADCRUMB_PROPS = null;
    };
  }, [breadcrumbs]);

  useEffect(() => {
    if (!breadcrumbs?.length && !breadcrumbData?.length)
      throw new Error('Breadcrumb data is required either in the page URL or the props of the Breadcrumbs component.');
  }, [breadcrumbData, breadcrumbs]);

  const rootBreadcrumb = useMemo<BreadcrumbData>(() => {
    // breadcrumbData from the URL takes precedence.
    if (breadcrumbData) {
      return breadcrumbData[0];
    }

    return (breadcrumbs || [])[0];
  }, [breadcrumbData, breadcrumbs]);

  const subBreadcrumbs = useMemo(() => {
    if (breadcrumbData) {
      return breadcrumbData.slice(1);
    }
    return (breadcrumbs || []).slice(1);
  }, [breadcrumbData, breadcrumbs]);

  // When the number of breadcrumbs are 5 or more, we show the rootBreadcrumb and the first item
  // in subBreadcrumbs. We also show the last 2 items in subBreadcrumbs. Everything else is collapsed
  // into a Menu.
  const hiddenSubBreadcrumbs = useMemo(() => {
    return subBreadcrumbs.filter((b, index) => index > 0 && index < subBreadcrumbs.length - 2);
  }, [subBreadcrumbs]);

  const toggleHiddenItemsMenu = useCallback(() => {
    setShowHiddenItems(prev => !prev);
  }, []);

  const handleHiddenMenuItemClick = useCallback(
    (b, index) => {
      const path = buildBreadcrumbUrl({
        path: b.path,
        // + 1 + 1 to account for the first item in subBreadcrumbs which isn't in the Menu.
        // The `index` parameter is relative to the hiddenBreadcrumbs array which is a subset
        // of the subBreadcrumbs array.
        breadcrumbs: [rootBreadcrumb, ...subBreadcrumbs.slice(0, index + 1 + 1)],
      });

      history.push(path);
    },
    [history, rootBreadcrumb, subBreadcrumbs],
  );
  // const iconMapping = {
  //   Insights: insights_selected,
  //   Scan: scan_selected,
  // };
  return (
    <div className="Breadcrumb">
      <Text
        element="h1"
        className="Breadcrumb__PageTitle"
        font="semi-bold"
        color={isDarkMode ? '#E2E8F0' : '#1A202C'}>
        {pageTitle ?? rootBreadcrumb?.title}
      </Text>

      {subBreadcrumbs.length > 0 && (
        <div className="Breadcrumb__Trail">
          <Text className="Breadcrumb__MainTitle" fontWeight="500" color={isDarkMode ? '#E2E8F0' : '#1A202C'}>
            <BreadcrumbIconMapping
              subtitle={subBreadcrumbs[0]?.title}
              title={rootBreadcrumb?.title}
            />
            {rootBreadcrumb?.path ? <Link to={rootBreadcrumb.path}>{rootBreadcrumb?.title}</Link> : rootBreadcrumb?.title}
          </Text>

          <ChevronIcon className="Breadcrumb__MainChevron" width="12" height="12" color="#919FA8" />

          {subBreadcrumbs?.map((b, index) => {
            return (
              <React.Fragment key={`${b.path}-${index}`}>
                {subBreadcrumbs.length > 4 ? (
                  <>
                    {index === 1 ? (
                      <>
                        <Text className="Breadcrumb__Ellipsis" ref={anchorElRef} onClick={toggleHiddenItemsMenu}>
                          ...
                        </Text>
                        <ChevronIcon className="Breadcrumb__Chevron" width="12" height="12" color="#919FA8" />
                      </>
                    ) : index > 1 && index < subBreadcrumbs.length - 2 ? null : (
                      <>
                        <Text style={{ fontSize: '14px' }} fontWeight="500" color={isDarkMode ? '#E2E8F0' : '#1A202C'}>
                          {index === subBreadcrumbs.length - 1 || !b.path ? (
                            b.title
                          ) : (
                            <Link
                              to={buildBreadcrumbUrl({
                                path: b.path,
                                breadcrumbs: [rootBreadcrumb, ...subBreadcrumbs.slice(0, index + 1)],
                              })}>
                              {b.title}
                            </Link>
                          )}
                        </Text>
                        {index < subBreadcrumbs.length - 1 ? (
                          <ChevronIcon className="Breadcrumb__Chevron" width="12" height="12" color="#919FA8" />
                        ) : null}
                      </>
                    )}
                  </>
                ) : (
                  <>
                    <Text style={{ fontSize: '14px' }} fontWeight="500" color={isDarkMode ? '#E2E8F0' : '#1A202C'}>
                      {index === subBreadcrumbs.length - 1 || !b.path ? (
                        b.title
                      ) : (
                        <Link
                          to={buildBreadcrumbUrl({
                            path: b.path,
                            breadcrumbs: [rootBreadcrumb, ...subBreadcrumbs.slice(0, index + 1)],
                          })}>
                          {b.title}
                        </Link>
                      )}
                    </Text>
                    {index < subBreadcrumbs.length - 1 ? (
                      <ChevronIcon className="Breadcrumb__Chevron" width="12" height="12" color="#919FA8" />
                    ) : null}
                  </>
                )}
              </React.Fragment>
            );
          })}
        </div>
      )}

      <Menu
        anchorEl={anchorElRef.current}
        open={showHiddenItems}
        onClose={toggleHiddenItemsMenu}
        PaperProps={{
          sx: {
            backgroundColor: isDarkMode ? '#2D3748' : '#FFF',
            border: `1px solid ${isDarkMode ? '#4A5568' : '#CBD5E0'}`,
            boxShadow: isDarkMode ? '0 4px 6px rgba(0, 0, 0, 0.2)' : '0 4px 6px rgba(0, 0, 0, 0.1)',
          },
        }}>
        {hiddenSubBreadcrumbs.map((b, index) => (
          <MenuItem
            key={`${b.path}-${index}`}
            className="Breadcrumb__HiddenMenuItem fs__2"
            onClick={() => handleHiddenMenuItemClick(b, index)}
            disabled={!b.path}>
            {b.title}
          </MenuItem>
        ))}
      </Menu>
    </div>
  );
}

export default Breadcrumbs;
